import { api } from 'core/api/ConnectionManager'
import {
    IScheduleDeviceDelta,
    IScheduleSidebarDevice,
    isGroupDevice,
    splitGroupToDevicesExcept,
    stripDevicesCoveredByGroup,
    toggleScheduleDevice,
} from './scheduleDeviceDelta'

export type ScheduleSelectable = {
    id: number
    name?: string
    type?: string
    selected?: boolean
}

export type SelectionChange = {
    delta: IScheduleDeviceDelta
    countDiff: number
    contribution?: number
    sidebarAdd?: ScheduleSelectable[]
    sidebarRemoveIds?: number[]
}

const CATALOG_SORT = [
    ['name', 'ASC'],
    ['type', 'ASC'],
    ['id', 'ASC'],
] as string[][]

const parseCatalogRows = (response: { data?: ScheduleSelectable[] } | ScheduleSelectable[]): ScheduleSelectable[] => {
    if (Array.isArray(response)) return response
    return Array.isArray(response?.data) ? response.data : []
}

const isAlreadySelected = (device: ScheduleSelectable, delta: IScheduleDeviceDelta, boundIds: Set<number>): boolean => {
    if (delta.removeDigitalSignageIds.includes(device.id)) return false
    if (delta.addDigitalSignageIds.includes(device.id)) return true
    if (boundIds.has(device.id)) return true
    return Boolean(device.selected)
}

const findParentSelectedGroupId = (
    deviceId: number,
    delta: IScheduleDeviceDelta,
    cache: Record<number, ScheduleSelectable[]>
): number | null => {
    for (const groupId of delta.addGroupIds) {
        if (cache[groupId]?.some((device) => device.id === deviceId)) return groupId
    }
    return null
}

/** Состав группы (в edit — с selected из getScheduleCatalog). */
export const fetchGroupDevices = async (
    groupId: number,
    scheduleId: number | null,
    cache: Record<number, ScheduleSelectable[]>
): Promise<ScheduleSelectable[]> => {
    let list: ScheduleSelectable[]

    if (scheduleId) {
        const response = await api.send<
            { parentId: number; scheduleId: number; limit: number; sort: string[][] },
            { data?: ScheduleSelectable[] } | ScheduleSelectable[]
        >('getScheduleCatalog', {
            parentId: groupId,
            scheduleId,
            limit: 1000,
            sort: CATALOG_SORT,
        })
        list = parseCatalogRows(response)
            .filter((item) => item.type !== 'group')
            .map((device) => ({ ...device, type: 'digitalSignage' }))
    } else {
        const devices = await api.send<{ groupId: number[]; digitalSignageId: number[] }, ScheduleSelectable[]>(
            'getDisplaysFromGroups',
            { groupId: [groupId], digitalSignageId: [] }
        )
        list = (Array.isArray(devices) ? devices : []).map((device) => ({
            ...device,
            type: 'digitalSignage',
        }))
    }

    cache[groupId] = list
    return list
}

/** Id устройств внутри выбранных групп — для галочек в каталоге. */
export const groupUiIds = (groupIds: number[], cache: Record<number, ScheduleSelectable[]>): number[] =>
    Array.from(new Set(groupIds.flatMap((groupId) => (cache[groupId] || []).map((device) => device.id))))

/**
 * Клик по группе.
 * previousContribution — сколько эта группа уже добавила в счётчик (нужно при снятии).
 */
export const applyGroupToggle = (
    delta: IScheduleDeviceDelta,
    groupId: number,
    selected: boolean,
    groupWasSelected: boolean,
    groupDevices: ScheduleSelectable[],
    boundIds: Set<number>,
    previousContribution = 0
): SelectionChange => {
    let next = toggleScheduleDevice(delta, { id: groupId, type: 'group', selected: groupWasSelected }, selected).delta

    if (selected) {
        next = stripDevicesCoveredByGroup(
            next,
            groupDevices.map((device) => device.id)
        )
        const newcomers = groupDevices.filter((device) => !isAlreadySelected(device, delta, boundIds))
        return {
            delta: next,
            countDiff: newcomers.length,
            contribution: newcomers.length,
            sidebarAdd: newcomers,
            sidebarRemoveIds: [groupId],
        }
    }

    return {
        delta: next,
        countDiff: -previousContribution,
        contribution: 0,
        sidebarRemoveIds: [
            groupId,
            ...groupDevices.filter((device) => !boundIds.has(device.id) && !device.selected).map((device) => device.id),
        ],
    }
}

/** Клик по устройству. Если оно внутри выбранной группы — группа распадается. */
export const applyDeviceToggle = (
    delta: IScheduleDeviceDelta,
    item: ScheduleSelectable,
    selected: boolean,
    boundIds: Set<number>,
    cache: Record<number, ScheduleSelectable[]>
): SelectionChange => {
    const parentGroupId = findParentSelectedGroupId(item.id, delta, cache)

    if (parentGroupId != null && !selected) {
        const groupDeviceIds = (cache[parentGroupId] || []).map((device) => device.id)
        return {
            delta: splitGroupToDevicesExcept(
                delta,
                parentGroupId,
                item.id,
                groupDeviceIds,
                (id) => boundIds.has(id) && !delta.removeDigitalSignageIds.includes(id)
            ),
            countDiff: -1,
            sidebarRemoveIds: [item.id],
        }
    }

    const result = toggleScheduleDevice(delta, item, selected)
    return {
        delta: result.delta,
        countDiff: result.countDiff,
        sidebarAdd: selected ? [item] : undefined,
        sidebarRemoveIds: selected ? undefined : [item.id],
    }
}

/** Удаление устройств из правого сайдбара. */
export const applySidebarRemovals = (
    delta: IScheduleDeviceDelta,
    currentSidebar: IScheduleSidebarDevice[],
    nextIds: Set<number>,
    boundIds: Set<number>,
    cache: Record<number, ScheduleSelectable[]>
): SelectionChange => {
    let next = delta
    let countDiff = 0

    currentSidebar.forEach((item) => {
        if (nextIds.has(item.id)) return

        const parentGroupId = findParentSelectedGroupId(item.id, next, cache)
        if (parentGroupId != null) {
            const groupDeviceIds = (cache[parentGroupId] || []).map((device) => device.id)
            next = splitGroupToDevicesExcept(
                next,
                parentGroupId,
                item.id,
                groupDeviceIds,
                (id) => boundIds.has(id) && !next.removeDigitalSignageIds.includes(id)
            )
            countDiff -= 1
            return
        }

        const addIds = isGroupDevice(item) ? next.addGroupIds : next.addDigitalSignageIds
        const wasOnSlot = !addIds.includes(item.id) && (Boolean(item.selected) || boundIds.has(item.id))
        const result = toggleScheduleDevice(next, { ...item, selected: wasOnSlot }, false)
        next = result.delta
        countDiff += result.countDiff
    })

    return { delta: next, countDiff }
}
